import {
  Feature,
  getIntervalPathParam,
  getQuotaPathParam,
  getRecurlySubUuIdPathParam,
  PlanInterval,
} from '../models';
import { useQuery } from '../routes';
import store from '../store';
import { track } from '../analytics';
import {
  PLAN_BILLING_QUERY_PARAM,
  PLAN_FEATURE_QUERY_PARAM,
  PLAN_QUANTITY_QUERY_PARAM,
} from './Constants';

// maximum amount that recurly allow for a payment
export const MAX_TOTAL_AMOUNT = 1000000;

export const BUSINESS_EMAIL = 'mailto:business@katalon.com';

type PlanProductQueryParam = {
  feature: Feature;
  quantity: number;
  cycle?: PlanInterval;
};

const parsePlanProductCycle = (value?: string | null): PlanInterval | undefined => {
  if (!value) {
    return undefined;
  }

  const normalizedValue = value.trim().toUpperCase();
  if (normalizedValue === 'ANNUAL') {
    return PlanInterval.YEAR;
  }
  if (normalizedValue === 'MONTHLY') {
    return PlanInterval.MONTH;
  }

  return PlanInterval.fromString(normalizedValue);
};

export const parsePlanProductQueryParams = (
  featureValue?: string | null,
  quantityValue?: string | null,
  billingValue?: string | null,
): PlanProductQueryParam | undefined => {
  const feature = Feature.fromString(featureValue?.trim().toUpperCase() ?? '');
  const quantity = quantityValue === undefined || quantityValue === null || quantityValue === ''
    ? 1
    : Number(quantityValue);

  if (!feature || !Number.isFinite(quantity) || quantity < 0) {
    return undefined;
  }

  return {
    feature,
    quantity: Math.floor(quantity),
    cycle: parsePlanProductCycle(billingValue),
  };
};

export const getPlanProductCycleFromPathParam = (): PlanInterval | undefined => {
  const { get } = useQuery();
  return parsePlanProductQueryParams(
    get(PLAN_FEATURE_QUERY_PARAM),
    get(PLAN_QUANTITY_QUERY_PARAM),
    get(PLAN_BILLING_QUERY_PARAM),
  )?.cycle;
};

export const getQuotaFromPathParam = (feature: Feature) => {
  const { get } = useQuery();
  const product = parsePlanProductQueryParams(
    get(PLAN_FEATURE_QUERY_PARAM),
    get(PLAN_QUANTITY_QUERY_PARAM),
    get(PLAN_BILLING_QUERY_PARAM),
  );
  const quota = get(getQuotaPathParam(feature));
  if (quota !== null) {
    const parsedQuota = Number(quota);
    return Number.isFinite(parsedQuota) ? parsedQuota : undefined;
  }
  return product?.feature === feature ? product.quantity : undefined;
};

export const getCycleFromPathParam = (feature: Feature): PlanInterval | undefined => {
  const { get } = useQuery();
  const product = parsePlanProductQueryParams(
    get(PLAN_FEATURE_QUERY_PARAM),
    get(PLAN_QUANTITY_QUERY_PARAM),
    get(PLAN_BILLING_QUERY_PARAM),
  );
  const interval = get(getIntervalPathParam(feature));
  if (interval) {
    return PlanInterval.fromString(interval);
  }
  return product?.feature === feature ? product.cycle : undefined;
};

// Pure helper — callers pass `get` from useQuery() so this stays callable inside
// effects and array callbacks (calling the hook here violated the rules of hooks).
export const getRecurlySubUuIdFromPathParam = (
  get: (name: string) => string | null,
  feature: Feature,
): string | undefined => get(getRecurlySubUuIdPathParam(feature)) || undefined;

export type TrackingPayload = Record<string, unknown>;

export const sendTrackingData = (
  eventName: string,
  accountId: number,
  organizationId?: number,
  additionalProps: TrackingPayload = {},
) => {
  const currentState = store.getState();
  const user = currentState.auth.user || undefined;
  const accountTotalMembers = Object.values(
    currentState.accountUsers.entities,
  ).filter(it => it !== undefined && it.accountId === accountId).length;

  track(
    eventName,
    {
      id: user?.id,
      user_id: user?.email,
      account_id: accountId,
      timestamp: new Date().toISOString(),
      ...(
        organizationId !== undefined
          ? { organization_id: organizationId }
          : {}
      ),
      role: user?.roles,
      total_members: accountTotalMembers,
      ...additionalProps,
    },
  );
};

export const formatUSPrice = (price: number) => price.toLocaleString('en-US');
